// ========================================
// WEBHOOK SERVICE - BACKEND-STORED CONFIG
// ========================================
// Sends alerts to Power Automate or other webhook endpoints
// Configuration is stored on the backend and shared across all users

import config from '../config';
import { authenticatedFetch } from './authService';

// Local cache for webhook config to reduce API calls
let cachedConfig = null;
let cacheTimestamp = 0;
const CACHE_TTL = 30000; // 30 seconds

/**
 * Get webhook configuration from backend API
 * Uses local cache to reduce API calls
 * @param {boolean} forceRefresh - If true, bypasses cache and fetches fresh data
 */
export const getWebhookConfig = async (forceRefresh = false) => {
    // Return cached config if still valid and not forcing refresh
    if (!forceRefresh && cachedConfig && Date.now() - cacheTimestamp < CACHE_TTL) {
        return cachedConfig;
    }

    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/webhook-config`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) {
                cachedConfig = result.data;
                cacheTimestamp = Date.now();
                return cachedConfig;
            }
        }
    } catch (error) {
        console.error('Error loading webhook config:', error);
    }

    // Return default config if API fails
    return getDefaultConfig();
};

/**
 * Get default webhook configuration
 */
const getDefaultConfig = () => ({
    enabled: false,
    url: '',
    alerts: {
        litellmDown: true,
        litellmDegraded: true,
        openwebuiDown: true,
        openwebuiDegraded: true,
        budgetWarning: true,
        budgetExceeded: true,
        teamBlocked: true,
        teamUnblocked: true,
        userDisabled: false,
        userEnabled: false,
        userAddedToTeam: false,
        userRemovedFromTeam: false,
    },
    thresholds: {
        latencyDegradedMs: 2000,
    },
    cooldownMinutes: 15
});

/**
 * Save webhook configuration to backend
 * @param {object} configUpdate - Partial config to update
 */
export const saveWebhookConfig = async (configUpdate) => {
    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/webhook-config`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(configUpdate)
        });

        if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) {
                // Update cache
                cachedConfig = result.data;
                cacheTimestamp = Date.now();
                return true;
            }
        }
        return false;
    } catch (error) {
        console.error('Error saving webhook config:', error);
        return false;
    }
};

/**
 * Invalidate the local cache to force a fresh fetch
 */
export const invalidateCache = () => {
    cachedConfig = null;
    cacheTimestamp = 0;
};

/**
 * Send an alert if enabled and not on cooldown.
 * Uses the atomic /send-if-ready endpoint which performs the cooldown check,
 * timestamp record, and delivery in a single locked backend operation — prevents
 * multiple browser tabs from each passing the cooldown check before any of them
 * records the timestamp (the old 3-round-trip race condition).
 */
const sendAlertIfEnabled = async (alertType, title, message, details = {}, severity = 'warning') => {
    const webhookConfig = await getWebhookConfig();

    // Fast path: skip the network call entirely if webhook is off or type is disabled
    if (!webhookConfig.enabled || !webhookConfig.url) {
        return { success: false, reason: 'Webhook not configured' };
    }
    if (!webhookConfig.alerts[alertType]) {
        return { success: false, reason: 'Alert type disabled' };
    }

    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/webhook-config/send-if-ready`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                alertType,
                severity,
                title,
                message,
                details
            })
        });

        const data = await response.json();

        if (data.skipped) {
            return { success: false, reason: data.reason };
        }
        if (data.success) {
            return { success: true };
        }
        return { success: false, reason: data.error || `HTTP ${response.status}` };
    } catch (error) {
        console.error('Webhook send error:', error);
        return { success: false, reason: error.message };
    }
};

// ========================================
// ALERT FUNCTIONS
// ========================================

/**
 * Report a ping result to the backend.
 * The backend holds the authoritative consecutive-failure counters (failCounts in
 * webhook-config.json) and fires health alerts atomically under a file lock.
 * All browser tabs and page reloads share the same counters.
 */
export const reportPing = async ({ litellmReachable, openwebuiReachable, litellmLatency, openwebuiLatency, unhealthyPercent, threshold }) => {
    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/webhook-config/report-ping`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ litellmReachable, openwebuiReachable, litellmLatency, openwebuiLatency, unhealthyPercent, threshold })
        });
        if (response.ok) return await response.json();
    } catch (error) {
        console.error('reportPing error:', error);
    }
    return { fired: [] };
};

/**
 * Check and send health-related alerts.
 * @deprecated — health alerts are now fired by the backend /report-ping endpoint.
 * Kept for reference; no longer called from App.js.
 */
export const checkAndSendHealthAlerts = async (healthData) => {
    const webhookConfig = await getWebhookConfig();
    if (!webhookConfig.enabled) return;

    const results = [];

    // LiteLLM health
    if (healthData.litellm) {
        const { isHealthy, isDegraded, latency } = healthData.litellm;

        if (!isHealthy) {
            results.push(await sendAlertIfEnabled(
                'litellmDown',
                'Ask Engine Down',
                'The Ask Engine (LiteLLM) service is not responding.',
                { service: 'LiteLLM', status: 'down' },
                'critical'
            ));
        } else if (isDegraded) {
            results.push(await sendAlertIfEnabled(
                'litellmDegraded',
                'Ask Engine Degraded',
                `Ask Engine is responding slowly (${latency}ms latency).`,
                { service: 'LiteLLM', status: 'degraded', latency },
                'warning'
            ));
        }
    }

    // OpenWebUI health
    if (healthData.openwebui) {
        const { isHealthy, isDegraded, latency } = healthData.openwebui;

        if (!isHealthy) {
            results.push(await sendAlertIfEnabled(
                'openwebuiDown',
                'Ask Front End Down',
                'The Ask Front End (OpenWebUI) service is not responding.',
                { service: 'OpenWebUI', status: 'down' },
                'critical'
            ));
        } else if (isDegraded) {
            results.push(await sendAlertIfEnabled(
                'openwebuiDegraded',
                'Ask Front End Degraded',
                `Ask Front End is responding slowly (${latency}ms latency).`,
                { service: 'OpenWebUI', status: 'degraded', latency },
                'warning'
            ));
        }
    }

    return results;
};

/**
 * Check and send budget-related alerts
 * @param {Array} teams - Array of team objects with budget info
 */
export const checkAndSendBudgetAlerts = async (teams, budgetThresholds) => {
    const webhookConfig = await getWebhookConfig();
    if (!webhookConfig.enabled) return;

    const results = [];

    for (const team of teams) {
        if (!team.max_budget || team.max_budget <= 0) continue;

        const usagePercent = (team.spend / team.max_budget) * 100;
        const alertThreshold = budgetThresholds?.alert || 90;
        const blockThreshold = budgetThresholds?.block || 100;
        const teamId = team.team_id || team.id || 'unknown';

        // Use per-team cooldown keys so two teams hitting budget in the same cooldown
        // window both get their own alert (old global key suppressed the second team).
        if (usagePercent >= blockThreshold) {
            results.push(await sendAlertIfEnabled(
                `budgetExceeded:${teamId}`,
                'Budget Exceeded',
                `Team "${team.team_alias}" has exceeded their budget limit.`,
                {
                    teamId,
                    teamName: team.team_alias,
                    spend: team.spend,
                    budget: team.max_budget,
                    usagePercent: Math.round(usagePercent),
                    blocked: team.blocked
                },
                'critical'
            ));
        } else if (usagePercent >= alertThreshold) {
            results.push(await sendAlertIfEnabled(
                `budgetWarning:${teamId}`,
                'Budget Warning',
                `Team "${team.team_alias}" is at ${Math.round(usagePercent)}% of their budget.`,
                {
                    teamId,
                    teamName: team.team_alias,
                    spend: team.spend,
                    budget: team.max_budget,
                    usagePercent: Math.round(usagePercent),
                    remaining: team.max_budget - team.spend
                },
                'warning'
            ));
        }
    }

    return results;
};

/**
 * Send team blocked alert
 */
export const sendTeamBlockedAlert = async (teamName, teamId, reason = 'Budget exceeded') => {
    // Per-team cooldown key so blocking two different teams within the same cooldown
    // window each get their own alert (a shared 'teamBlocked' key suppressed the second).
    return await sendAlertIfEnabled(
        `teamBlocked:${teamId}`,
        'Team Blocked',
        `Team "${teamName}" has been blocked.`,
        { teamId, teamName, reason },
        'critical'
    );
};

/**
 * Send team unblocked alert
 */
export const sendTeamUnblockedAlert = async (teamName, teamId, unblockedBy) => {
    return await sendAlertIfEnabled(
        `teamUnblocked:${teamId}`,
        'Team Unblocked',
        `Team "${teamName}" has been unblocked by ${unblockedBy}.`,
        { teamId, teamName, unblockedBy },
        'info'
    );
};

/**
 * Send user disabled alert
 */
export const sendUserDisabledAlert = async (userName, userEmail, disabledBy) => {
    return await sendAlertIfEnabled(
        'userDisabled',
        'User Account Disabled',
        `User "${userName}" (${userEmail}) has been disabled by ${disabledBy}.`,
        { userName, userEmail, disabledBy },
        'warning'
    );
};

/**
 * Send user enabled alert
 */
export const sendUserEnabledAlert = async (userName, userEmail, enabledBy) => {
    return await sendAlertIfEnabled(
        'userEnabled',
        'User Account Enabled',
        `User "${userName}" (${userEmail}) has been enabled by ${enabledBy}.`,
        { userName, userEmail, enabledBy },
        'info'
    );
};

/**
 * Send user added to team alert
 */
export const sendUserAddedToTeamAlert = async (userName, userEmail, teamName, addedBy) => {
    return await sendAlertIfEnabled(
        'userAddedToTeam',
        'User Added to Team',
        `User "${userName}" has been added to team "${teamName}" by ${addedBy}.`,
        { userName, userEmail, teamName, addedBy },
        'info'
    );
};

/**
 * Send user removed from team alert
 */
export const sendUserRemovedFromTeamAlert = async (userName, userEmail, teamName, removedBy) => {
    return await sendAlertIfEnabled(
        'userRemovedFromTeam',
        'User Removed from Team',
        `User "${userName}" has been removed from team "${teamName}" by ${removedBy}.`,
        { userName, userEmail, teamName, removedBy },
        'info'
    );
};

/**
 * Test the webhook configuration
 */
export const testWebhook = async () => {
    const webhookConfig = await getWebhookConfig();

    if (!webhookConfig.url) {
        return { success: false, reason: 'No webhook URL configured' };
    }

    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/webhook-config/send`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                timestamp: new Date().toISOString(),
                source: 'Ask Operations Dashboard',
                alertType: 'test',
                severity: 'info',
                title: 'Webhook Test',
                message: 'This is a test message from the Ask Operations Dashboard.',
                details: {
                    test: true,
                    configuredAlerts: Object.entries(webhookConfig.alerts)
                        .filter(([, enabled]) => enabled)
                        .map(([key]) => key)
                }
            })
        });

        const data = await response.json();
        if (data.success) {
            return { success: true };
        } else {
            const reason = data.detail
                ? `${data.error} — ${data.detail}`
                : (data.error || `HTTP ${response.status}`);
            return { success: false, reason };
        }
    } catch (error) {
        return { success: false, reason: error.message };
    }
};

/**
 * Clear all cooldown timers
 */
export const clearCooldowns = async () => {
    try {
        const response = await authenticatedFetch(
            `${config.backendApiUrl}/webhook-config/clear-cooldowns`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            }
        );

        if (response.ok) {
            const result = await response.json();
            return result.success;
        }
        return false;
    } catch (error) {
        console.error('Error clearing cooldowns:', error);
        return false;
    }
};

/**
 * Get human-readable alert type names
 */
export const getAlertTypeLabels = () => ({
    // Service health
    litellmDown: 'Ask Engine Down',
    litellmDegraded: 'Ask Engine Degraded (Slow)',
    openwebuiDown: 'Ask Front End Down',
    openwebuiDegraded: 'Ask Front End Degraded (Slow)',
    // Budget
    budgetWarning: 'Team Budget Warning',
    budgetExceeded: 'Team Budget Exceeded',
    teamBlocked: 'Team Blocked',
    teamUnblocked: 'Team Unblocked',
    // User management
    userDisabled: 'User Account Disabled',
    userEnabled: 'User Account Enabled',
    userAddedToTeam: 'User Added to Team',
    userRemovedFromTeam: 'User Removed from Team',
});

/**
 * Get alert categories for UI grouping
 */
export const getAlertCategories = () => ({
    'Service Health': ['litellmDown', 'litellmDegraded', 'openwebuiDown', 'openwebuiDegraded'],
    'Budget & Teams': ['budgetWarning', 'budgetExceeded', 'teamBlocked', 'teamUnblocked'],
    'User Management': ['userDisabled', 'userEnabled', 'userAddedToTeam', 'userRemovedFromTeam'],
});

const webhookService = {
    getWebhookConfig,
    saveWebhookConfig,
    invalidateCache,
    reportPing,
    checkAndSendHealthAlerts,
    checkAndSendBudgetAlerts,
    sendTeamBlockedAlert,
    sendTeamUnblockedAlert,
    sendUserDisabledAlert,
    sendUserEnabledAlert,
    sendUserAddedToTeamAlert,
    sendUserRemovedFromTeamAlert,
    testWebhook,
    clearCooldowns,
    getAlertTypeLabels,
    getAlertCategories,
};

export default webhookService;
